<template>
    <div class="InvitationsList">
        <AppTable
            ref="table"
            :columns="cols"
            :data="normalizeRows"
            :total="total"
            :loading="loading"
            hideHeader
            rowKey="uuid"
            class="Table"
            @table-request="handleFetchData"
        >
            <template #content="{ row }">
                <q-card
                    bordered
                    class="InvitationCard"
                >
                    <q-card-section class="InvitationCardHeader bg-primary text-white">
                        <div class="InvitationCardHeaderRow flex row-stretch-center no-wrap">
                            <q-icon
                                name="person"
                                color="white"
                                size="22px"
                                class="InvitationPersonIcon"
                            />
                            <div class="InvitationCardTitle ellipsis">
                                {{ row.email }}
                            </div>
                            <q-btn
                                v-if="(row.options || []).length"
                                icon="more_vert"
                                color="white"
                                dense
                                flat
                                @click.stop="openMenu(row, $event)"
                            />
                        </div>
                    </q-card-section>

                    <q-card-section class="InvitationCardBody bg-white">
                        <div class="InvitationCardBodyLayout">
                            <div class="InvitationCardBodyContent">
                                <div class="InvitationCardRow flex row-left-center no-wrap">
                                    <div class="text-caption q-mr-sm">
                                        {{ $t('general.status') }}:
                                    </div>
                                    <QBadge :color="getBadgeType(row.status)">
                                        {{ $t(`invitations.${row.status}`) }}
                                    </QBadge>
                                </div>

                                <div class="InvitationCardRow">
                                    <div class="text-caption">
                                        {{ $t('general.createdAt') }}
                                    </div>
                                    <div class="text-body2">
                                        {{ App.dateLocationFormat(getDate(row), 'long') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </q-card-section>
                </q-card>
            </template>

            <template #empty>
                <EmptyList
                    :title="$t('invitations.notifications.noResult.title')"
                    :text="$t('invitations.notifications.noResult.text')"
                    class="mt-2 mb-2"
                >
                    <template #footer>
                        <QBtn
                            color="primary"
                            noCaps
                            :label="$t('invitations.new')"
                            icon="add_circle_outline"
                            @click="$emit('onNewItem')"
                        />
                    </template>
                </EmptyList>
            </template>
        </AppTable>

        <q-menu
            v-if="menuActivator"
            v-model="menuOpen"
            :target="menuActivator"
            @hide="cleanSelected"
        >
            <q-list dense class="text-caption">
                <q-item
                    v-for="option in menuOptions"
                    :key="option.key"
                    clickable
                    @click="handleClickOption(option)"
                >
                    <q-item-section avatar>
                        <q-icon :name="option.icon" :color="option.iconColor" />
                    </q-item-section>
                    <q-item-section>
                        {{ option.label }}
                    </q-item-section>
                </q-item>
            </q-list>
        </q-menu>
    </div>
</template>

<script>
/* eslint-disable func-names */
import VueTypes from 'vue-types'
import { Table as AppTable, EmptyList } from '@/components'
import { filtersMixin } from '@/mixins'

const STATES = {
    INVITED: 'INVITED',
    ACCEPTED: 'ACCEPTED',
    CANCELLED: 'CANCELLED',
    REVOKED: 'REVOKED',
}

export default {
    name: 'InvitationsList',
    components: {
        AppTable,
        EmptyList,
    },
    mixins: [filtersMixin],
    inject: ['App'],
    props: {
        columns: VueTypes.array,
        rows: VueTypes.array,
        total: VueTypes.number,
        search: VueTypes.string,
        loading: VueTypes.bool.def(false),
    },
    emits: ['onNewItem', 'onFetchData', 'onRevokeInvitation', 'onCancelInvitation'],
    data() {
        return {
            selected: undefined,
            menuOpen: false,
            menuActivator: null,
            menuOptions: [],
        }
    },
    computed: {
        cols() {
            return [
                ...this.columns,
                {
                    name: 'status',
                    label: this.$t('general.status'),
                    align: 'left',
                },
                {
                    name: 'email',
                    label: this.$t('general.email'),
                    align: 'left',
                    width: 150,
                },
                {
                    name: 'date',
                    label: this.$t('general.createdAt'),
                    align: 'left',
                },
                {
                    name: 'actions',
                    label: '',
                    align: 'right',
                },
            ]
        },
        normalizeRows() {
            return (this.rows || []).map((row) => ({
                ...row,
                ...this.generateOptionsRow(row),
            }))
        },
    },
    methods: {
        handleFetchData(props) {
            this.$emit('onFetchData', props)
        },
        generateOptionsRow({ status }) {
            return {
                options: [
                    ...(status === STATES.INVITED
                        ? [{
                            key: 'REVOKE',
                            icon: 'close',
                            iconColor: 'negative',
                            label: this.$t('invitations.revoke'),
                            event: 'onRevokeInvitation',
                        }]
                        : []),
                    ...(status === STATES.ACCEPTED
                        ? [{
                            key: 'CANCEL',
                            icon: 'close',
                            iconColor: 'negative',
                            label: this.$t('invitations.cancel'),
                            event: 'onCancelInvitation',
                        }]
                        : []),
                ],
            }
        },
        getPagination() {
            return this.$refs.table.getPagination()
        },
        getBadgeType(value) {
            const types = {
                [STATES.INVITED]: 'info',
                [STATES.ACCEPTED]: 'success',
                [STATES.REVOKED]: 'error',
                [STATES.CANCELLED]: 'error',
                default: 'primary',
            }
            return types[value] || types.default
        },
        getDate(row) {
            const types = {
                [STATES.INVITED]: row.createdAt,
                [STATES.ACCEPTED]: row.acceptedAt,
                [STATES.REVOKED]: row.revokedAt,
                [STATES.CANCELLED]: row.revokedAt,
            }
            return types[row.status]
        },
        openMenu(row, event) {
            this.selected = row
            this.menuActivator = event?.currentTarget || event?.target || null
            this.menuOptions = row?.options || []

            if (!this.menuActivator) return
            this.menuOpen = true
        },
        cleanSelected() {
            this.menuOpen = false
            this.selected = undefined
            this.menuActivator = null
            this.menuOptions = []
        },
        handleClickOption({ event }) {
            if (event) this.$emit(event, this.selected?.uuid)
            this.cleanSelected()
        },
    },

}
</script>
<style scoped lang="scss">
.Table:deep() {
    border: none !important;

    .q-table__middle {
        overflow: visible;
    }

    .q-table__bottom {
        margin-top: 20px;
        border: 1px solid $table-border-color;
    }

    tbody {
        display: grid;
        grid-template-columns: repeat(1, minmax(0, 1fr));
        grid-gap: 16px;
        padding: 2px 2px 10px;
        overflow: visible;

        @media screen and (min-width: $md) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        @media screen and (min-width: $xl) {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        @media screen and (min-width: $xxxl) {
            grid-template-columns: repeat(4, minmax(0, 1fr));
        }
    }
}

.InvitationCard {
    width: 100%;
    height: 100%;
}

.InvitationCardHeader {
    padding: 12px 16px;
}

.InvitationCardTitle {
    font-weight: 500;
}

.ellipsis {
    display: inline-block;
    vertical-align: middle;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.InvitationCardBody {
    padding: 12px 16px;
}

.InvitationCardBodyLayout {
    display: flex;
    align-items: flex-start;
}

.InvitationPersonIcon {
    margin-right: 12px;
    margin-top: 2px;
}

.InvitationCardBodyContent {
    flex: 1;
    min-width: 0;
}

.InvitationCardRow {
    & + & {
        margin-top: 8px;
    }
}
</style>
